// Weekly Reports section on the main dashboard.
// Lists the weeks that have a report (newest first, from the API) and links each
// out to the server-rendered report page. The API returns a full tokenized `url`
// per week (the report page is HMAC-token-gated), so we link to that verbatim — a
// plain <a> navigation, since the page is rendered by the Worker, not this SPA.
// Hidden entirely when the user has no reports yet.

import type { WeeklyReportSummary } from '../lib/types'
import type { Strings } from '../lib/i18n'

interface Props {
  agentId: string
  reports: WeeklyReportSummary[]
  strings: Strings
}

// Fallback URL (untokenized) for older cached responses that predate the `url`
// field. Prod responses always include the tokenized url.
function reportHref(agentId: string, r: WeeklyReportSummary): string {
  return r.url || `/me/${encodeURIComponent(agentId)}/weekly/${encodeURIComponent(r.week)}`
}

// "Jun 8 – Jun 14" from the summary's date range, falling back to the ISO week.
function rangeLabel(r: WeeklyReportSummary, s: Strings): string {
  if (r.week_start && r.week_end) return `${s.fmtDate(r.week_start)} – ${s.fmtDate(r.week_end)}`
  if (r.week_start) return s.fmtDate(r.week_start)
  return r.week
}

export function WeeklyReports({ agentId, reports, strings: s }: Props) {
  if (!reports || reports.length === 0) return null

  return (
    <div className="dash-card dash-weekly-card">
      <div className="dash-card-title">{s.weeklyReportsTitle}</div>
      <ul className="weekly-list">
        {reports.map((r) => (
          <li key={r.week} className="weekly-item">
            <a
              className="weekly-link"
              href={reportHref(agentId, r)}
              aria-label={`${s.weeklyReportOpen}: ${rangeLabel(r, s)}`}
            >
              <span className="weekly-range">{rangeLabel(r, s)}</span>
              <span className="weekly-week">{r.week}</span>
              <span className="weekly-chevron" aria-hidden="true">›</span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}
